// src/contexts/ApiAuthContext.jsx
import React, { createContext, useContext, useState, useEffect } from 'react';
import { authService, userService } from '../features/users';
import { tokenManager } from '../core/api/client';
import { toast } from 'react-hot-toast';

const ApiAuthContext = createContext(null);

const FALLBACK_ROLE_PERMISSIONS = {
  admin: {
    'dashboard.view': true,
    'projects.view': true,
    'tasks.view': true,
    'team.view': true,
    'resources.view': true,
    'reports.view': true,
    'marketing.view': true,
    'pr.view': true,
    'graphics.view': true,
    'calendar.view': true,
    'competitions.view': true,
    'clients.view': true,
    'admin.approveMembers': true,
    'admin.managePermissions': true,
  },
  community_member: {
    'dashboard.view': true,
    'projects.view': true,
    'tasks.view': true,
    'resources.view': true,
    'calendar.view': true,
    'team.view': true,
  },
  developer: {
    'dashboard.view': true,
    'projects.view': true,
    'tasks.view': true,
    'resources.view': true,
    'calendar.view': true,
    'reports.view': true,
    'team.view': true,
  },
  leader: {
    'dashboard.view': true,
    'projects.view': true,
    'tasks.view': true,
    'resources.view': true,
    'calendar.view': true,
    'team.view': true,
    'reports.view': true,
    'competitions.view': true,
  },
  supervisor: {
    'dashboard.view': true,
    'projects.view': true,
    'tasks.view': true,
    'resources.view': true,
    'calendar.view': true,
    'team.view': true,
    'reports.view': true,
    'marketing.view': true,
    'pr.view': true,
    'competitions.view': true,
    'admin.approveMembers': true,
  },
  hod: {
    'dashboard.view': true,
    'projects.view': true,
    'tasks.view': true,
    'resources.view': true,
    'calendar.view': true,
    'team.view': true,
    'reports.view': true,
    'marketing.view': true,
    'pr.view': true,
    'competitions.view': true,
    'admin.approveMembers': true,
  },
  resource_manager: {
    'dashboard.view': true,
    'projects.view': true,
    'tasks.view': true,
    'resources.view': true,
    'calendar.view': true,
    'reports.view': true,
  },
  graphics: {
    'dashboard.view': true,
    'projects.view': true,
    'tasks.view': true,
    'resources.view': true,
    'calendar.view': true,
    'graphics.view': true,
  },
  marketing: {
    'dashboard.view': true,
    'projects.view': true,
    'tasks.view': true,
    'resources.view': true,
    'calendar.view': true,
    'marketing.view': true,
    'reports.view': true,
  },
  pr: {
    'dashboard.view': true,
    'projects.view': true,
    'tasks.view': true,
    'resources.view': true,
    'calendar.view': true,
    'pr.view': true,
    'reports.view': true,
  },
  client: {
    'dashboard.view': true,
    'projects.view': true,
  },
};

const resolveRolePermissions = (role, permissions) => {
  if (permissions && typeof permissions === 'object' && Object.keys(permissions).length > 0) {
    return permissions;
  }

  return FALLBACK_ROLE_PERMISSIONS[role] || {};
};

export const ApiAuthProvider = ({ children }) => {
  const [user, setUser] = useState(null);
  const [userRole, setUserRole] = useState(null);
  const [rolePermissions, setRolePermissions] = useState({});
  const [loading, setLoading] = useState(true);

  // Load user on mount if token exists
  useEffect(() => {
    const loadUser = async () => {
      const token = tokenManager.getToken();
      
      if (token) {
        try {
          const userData = await authService.getCurrentUser();
          setUser(userData);
          setUserRole(userData.role);

          setRolePermissions(resolveRolePermissions(userData.role, userData.permissions));
        } catch (error) {
          console.error('Failed to load user:', error);
          tokenManager.clearTokens();
        }
      }
      
      setLoading(false);
    };

    loadUser();
  }, []);

  const login = async (email, password) => {
    try {
      const { user: userData } = await authService.login(email, password);
      
      setUser(userData);
      setUserRole(userData.role);

      setRolePermissions(resolveRolePermissions(userData.role, userData.permissions));
      
      toast.success('Logged in successfully');
      return userData;
    } catch (error) {
      console.error('Login error:', error);
      const message = error.detail || error.message || 'Login failed';
      toast.error(message);
      throw error;
    }
  };

  const signup = async (userData) => {
    try {
      const response = await authService.signup(userData);
      
      if (response.requires_approval) {
        toast.success('Account created! Awaiting admin approval.');
      } else if (response.user) {
        // Immediately active user (admin/client)
        setUser(response.user);
        setUserRole(response.user.role);
        setRolePermissions(resolveRolePermissions(response.user.role, response.user.permissions));
        toast.success('Account created successfully!');
      }
      
      return response;
    } catch (error) {
      console.error('Signup error:', error);
      const message = error.detail || error.message || 'Signup failed';
      toast.error(message);
      throw error;
    }
  };

  const logout = async () => {
    try {
      await authService.logout();
      setUser(null);
      setUserRole(null);
      setRolePermissions({});
      toast.success('Logged out successfully');
    } catch (error) {
      console.error('Logout error:', error);
      throw error;
    }
  };

  const updateProfile = async (updates) => {
    try {
      const updatedUser = await userService.updateMyProfile(updates);
      setUser(updatedUser);
      toast.success('Profile updated successfully');
      return updatedUser;
    } catch (error) {
      console.error('Update profile error:', error);
      const message = error.detail || error.message || 'Failed to update profile';
      toast.error(message);
      throw error;
    }
  };

  const changePassword = async (currentPassword, newPassword) => {
    try {
      await authService.changePassword(currentPassword, newPassword);
      toast.success('Password changed successfully');
    } catch (error) {
      console.error('Change password error:', error);
      const message = error.detail || error.message || 'Failed to change password';
      toast.error(message);
      throw error;
    }
  };

  // Check if user has specific permission
  const hasPermission = (permissionId) => {
    if (userRole === 'admin') return true;
    return rolePermissions[permissionId] === true;
  };

  // Check if user has a specific role (accepts single string or array)
  const hasRole = (role) => {
    if (!userRole) return false;
    if (Array.isArray(role)) return role.includes(userRole);
    return userRole === role;
  };

  const value = {
    user,
    userRole,
    rolePermissions,
    login,
    signup,
    logout,
    updateProfile,
    changePassword,
    hasPermission,
    hasRole,
    loading
  };

  return (
    <ApiAuthContext.Provider value={value}>
      {children}
    </ApiAuthContext.Provider>
  );
};

export const useApiAuth = () => {
  const context = useContext(ApiAuthContext);
  if (!context) {
    throw new Error('useApiAuth must be used within an ApiAuthProvider');
  }
  return context;
};
